import { generateClassName } from '@klv/shared/utils/functions';
import { gtmButtonEvent } from '@klv/shared/utils/google-tag-manager';
import Icon from '@klv/atoms/icon';
import * as s from '../styles/button.module.scss';

import type { IButtonProps, ISharedProps } from '../types';

const Button = ({
  action, buttonColor = 'dark', children, className, rightIcon, leftIcon, iconColor, setTabIndex = 0, text,
  variant = 'primary', isDisabled, ...rest
}: IButtonProps) => {
  
  const cssClasses = [s[buttonColor], className];
  if (variant === 'none') {
    cssClasses.push(s.none);
  } else {
    cssClasses.push(s.button);
    cssClasses.push(s[variant]);
  }

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    let element = event.target as HTMLElement | null;
    let dataAttribute;

    while (element) {
      if (element.hasAttribute('data-organism')) {
        dataAttribute = element.getAttribute('data-organism');
        break;
      }
      element = element.parentElement;
    }

    if (dataAttribute) {
      gtmButtonEvent({
        category: `Button Click - ${dataAttribute}`,
        action: 'Button Click',
        label: text,
        data_organism: dataAttribute,
        variant,
      });
    }
    if (action) {
      action();
    }
  };
  
  const sharedProps: ISharedProps = {
    'aria-label': text && setTabIndex > -1 ? text : '',
    className: generateClassName(cssClasses),
    onClick: handleClick,
    role: 'button',
    tabIndex: text ? setTabIndex : -1,
    variant,
    disabled: isDisabled ? true : undefined,
  };

  // only set aria-hidden if text is not provided or setTabIndex is less than 0
  if (!!(!text || setTabIndex < 0)) {
    sharedProps['aria-hidden'] = true;
  }
  const rightIconName = rightIcon?.iconName; 
  const leftIconName = leftIcon?.iconName;
  const colorIcon = iconColor ? `var(--color-${iconColor})` : 'currentColor';
  let textContent = <button dangerouslySetInnerHTML={{ __html: text || '' }} {...sharedProps} {...rest} />;
  if ((rightIconName || leftIconName) && text) {
    const leftIconToUse = leftIconName && <Icon iconName={leftIconName} />;
    const rightIconToUse = rightIconName && <Icon iconName={rightIconName} />;
    const newClassName = generateClassName([sharedProps.className, s.iconRow]);
    textContent = <button data-variant={variant} {...sharedProps} className={newClassName} style={{
      '--color-button-icon-fill': colorIcon,
    } as React.CSSProperties} {...rest}>{leftIconToUse}<span dangerouslySetInnerHTML={{ __html: text }}/>{rightIconToUse}</button>;
  }
  return (text
    ? (textContent)
    : (<button data-variant={variant} {...sharedProps} {...rest}>{children}</button>)
  );
};

export default Button;
